import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  PointerEvent,
  Dispatch,
  SetStateAction,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Theme, alpha } from "@mui/material";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IDispatchDeskExpanded } from "src/redux/reducers/DispatchDeskReducer";
import {
  IAircraftFlights,
  InteractiveState,
  isFlight,
  ITimeIntervalLoaded,
  defaultAircraftQuickFilter,
  noAircraftSelected,
} from "src/redux/reducers/FlightTimelineReducer";
import {
  retrieveFlightData,
  updateSelectedFlight,
  updateSelectedGroundEvent,
} from "src/redux/actions/flights/FlightData.Actions";
import { closeModal, openModal, openSnackbar, updateUserAppSettings } from "src/redux/actions/App.Actions";
import {
  setInteractiveState,
  setIsFlightContextOpen,
  setSelectedEvents,
  setSelectedDay,
  setSelectedAircraft,
  setBulkAssignmentMode,
  setHoveredEvent,
  setHoveredTime,
  setUserSetScale,
  setFocusedEvents,
  setHoveredGroundTimeInterval,
  setCrewConnections,
  setTimelineGLs,
  setIsAircraftContextOpen,
  setAircraftContextId,
  setQuickFilterOrder,
} from "src/redux/actions/flights/FlightTimeline.Actions";
import { TimelineGL } from "./gl/TimelineGL";
import { useTheme } from "@mui/styles";
import moment from "moment-timezone";
import { timeScales, indexOfClosest, binarySearchAircraftEvents, searchGroundEvents } from "./TimelineUtils";
import {
  IFlight,
  IAircraft,
  IGroundEvent,
  ScrollBarClick,
  IImportFlight,
  IUserAppSettings,
  hourInMilliseconds,
  IAircraftRowSetting,
} from "@vmosolutions/oc-interfaces";
import { useDrop, DropTargetMonitor } from "react-dnd";

import { TimelineTime } from "./TimelineTime";
import { FlightTimelineHeader } from "./FlightTimelineHeader/FlightTimelineHeader";
import { Typography } from "@mui/material";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { HoveredTime } from "./HoveredTime";
import { SelectedFlightsDragLayer } from "src/components/CrewSchedule/SelectedFlightsDragLayer";
import { cloneDeep, debounce } from "lodash";
import {
  applyAircraftFilter,
  applyQuickAircraftFilter,
  convertSelectedFiltersToFilterOptions,
  selectedAircraftIdsIn,
  moveAircraftInOrder,
  sortFilteredAircraft,
} from "./FlightTimelineHeader/TimelineSortFilter";
// Manage Aircraft Configurations
import { convertAircraftToFlightAircraft } from "src/redux/reducers/FlightTimelineReducer";
import FeaturedPlayListIcon from "@mui/icons-material/FeaturedPlayList";
import { IconButton } from "src/components/IconButton/IconButton";
import { ArrowRight, ArrowLeft } from "@mui/icons-material";
import { AircraftIdentityRow } from "./AircraftIdentityRow";
import { FlightTimelineDatePicker } from "./FlightTimelineDatePicker";
import { checkOS } from "src/utils";

interface IFlightTimelineProps {
  windowId: string;
  path: string[];
  setActiveEventModal: (activeEventModal: EventModalTypes) => void;
  ref?: any;
  hideHoveredTimeIndicator?: boolean;
  setIsActive: Dispatch<SetStateAction<boolean>>;
}

export interface IDomain {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

const timelineStyles = (): any => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  flexGrow: 1,
  flexBasis: 0,
});

const timelineHeaderStyles = (theme: Theme): any => ({
  display: "flex",
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  "& .timeline-button-container": {
    display: "flex",
    flexGrow: 0,
    flexDirection: "row",
  },
  "& .search_timeline": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    "& .advanced_search_button": {
      height: "fit-content",
      margin: "12px",
    },
    "& .advanced_search_icon": {
      marginRight: "10px",
    },
  },
});

const flightHighlightBarStyles = (theme: Theme): any => ({
  position: "absolute",
  zIndex: 1,
  width: 24,
  height: "calc(100% - 60px)",
  left: 0,
  top: 60,
  backgroundColor: alpha(theme.custom.timeline.webglCanvas.background, 0.4),
  overflow: "clip",
  "&.active": {
    "& .highlight_toggle_btn": { opacity: 1 },
    "& .highlight_label": { opacity: 1 },
  },
  "& .highlight_toggle_btn": {
    padding: 0,
    opacity: 0.5,
    "& svg": {
      width: 20,
      height: 16,
      margin: "2px",
      "& path": { fill: theme.palette.primary.contrastText },
    },
  },
  "& .highlight_label": {
    opacity: 0.5,
    userSelect: "none",
    display: "block",
    color: theme.palette.primary.contrastText,
    transform: "rotate(90deg)",
    transformOrigin: "bottom left",
    width: 150,
    height: 20,
    marginTop: "-12px",
  },
});

const aircraftColumnWidth = 86;
const aircraftColumnPadding = 6;
// Optional columns that sit beside the aircraft identity chip. Must match `.aircraft_status` / `.aircraft_maintenance_counters`.
const aircraftStatusWidth = 100;
const maintenanceCountersWidth = 44;
const aircraftInfoWidth = 300;

const timelineBodyStyles = (theme: Theme, flightRowSettings: IAircraftRowSetting): any => ({
  width: "calc(100% - 5px)",
  margin: "0px 5px 5px 0px",
  display: "flex",
  flexGrow: 1,
  // Added to help indicate bottom and right edges of the timeline.
  overflow: "hidden",

  "& .timeline_content": {
    color: "white",
    minWidth: "400px",
    flexWrap: "nowrap",

    "& .timeline_aircraft_column": {
      display: "flex",
      width: aircraftColumnWidth,
      paddingLeft: `${aircraftColumnPadding}px`, // reduced now that content in aircraft column is right justified.
      position: "relative",
      backgroundColor: theme.palette.background.paper,

      "&.expanded": {
        width: aircraftColumnWidth + aircraftStatusWidth,
      },
      "&.mx_counters_visible": {
        width: aircraftColumnWidth + maintenanceCountersWidth,
      },
      "&.expanded.mx_counters_visible": {
        width: aircraftColumnWidth + aircraftStatusWidth + maintenanceCountersWidth,
      },

      "& .toggle_aircraft_status_btn": {
        height: 15,
        width: 15,
        borderRadius: 0,
        margin: "35px 0px 5px 5px",
        padding: 0,
        "& svg": { color: theme.palette.primary.light },
      },
      "& .status_title": {
        color: theme.palette.primary.light,
        fontSize: 11,
        marginTop: "34px",
        textTransform: "uppercase",
      },

      // Aircraft Registration/fleet identity.
      "& .aircraft_identity": {
        border: "2px",
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        width: "inherit",
        overflow: "hidden",
        flexBasis: 0,

        "& .aircraft_details": {
          display: "flex",
          height: flightRowSettings.aircraftRowTotalHeight,
          fontFamily: "Heebo, sans-serif",
          transition: theme.transitions.create("border", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
          width: aircraftColumnWidth - aircraftColumnPadding, // Aircraft Column Width minus padding
          "& > div": {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "100%",
            height: flightRowSettings.aircraftRowTotalHeight,
            userSelect: "none",
            borderBottom: "1px solid #1a1a1a",
            "& .aircraft_text": {
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-end",
              flexGrow: 1,
              minWidth: 0,
              height: "100%",
              position: "relative",
              // Quick filter free mode drag handle (ENG-715). Overlays the (normally empty) left side of the
              // right-justified text so it costs no width; only shown while hovering a reorderable row.
              "& .aircraft_drag_handle": {
                position: "absolute",
                left: -2,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: Math.min(Math.max(Math.round(flightRowSettings.aircraftRowTotalHeight * 0.4), 12), 18),
                color: theme.palette.text.secondary,
                opacity: 0,
                transition: "opacity 0.15s ease-in-out",
              },
            },
            "& .aircraft_icons": {
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "1px",
              paddingRight: "2px",
              height: "100%",
              opacity: 0,
              transition: "opacity 0.15s ease-in-out",
              "& .aircraft_icon": {
                fontSize: Math.max(Math.round(flightRowSettings.aircraftRowTotalHeight * 0.36), 14),
                color: theme.palette.text.secondary,
                cursor: "pointer",
                borderRadius: "2px",
                padding: "1px",
                "&:hover": {
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              },
              "& .aircraft_icon_disabled": {
                opacity: 0.3,
                cursor: "not-allowed",
                "&:hover": {
                  color: theme.palette.text.secondary,
                  backgroundColor: "transparent",
                },
              },
            },
            "& .aircraft_reg": {
              color: theme.custom.timeline.aircraftRegColor,
              fontSize: Math.round(flightRowSettings.aircraftRowTotalHeight * 0.33),
              lineHeight: `${flightRowSettings.aircraftRowTotalHeight * 0.33}px`,
              textAlign: "right",
              transition: theme.transitions.create("color", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
            },
            "& .aircraft_fleet": {
              color: theme.custom.timeline.aircraftFleetColor,
              fontSize: Math.round(flightRowSettings.aircraftRowTotalHeight * 0.29),
              lineHeight: `${flightRowSettings.aircraftRowTotalHeight * 0.29}px`,
              textAlign: "right",
              transition: theme.transitions.create("color", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
            },
            "& .aircraft_type": {
              color: theme.custom.timeline.aircraftTypeColor,
              fontSize: Math.round(flightRowSettings.aircraftRowTotalHeight * 0.26),
              lineHeight: `${flightRowSettings.aircraftRowTotalHeight * 0.26}px`,
              textAlign: "right",
            },
            "& .aircraft_serial": {
              color: theme.custom.timeline.aircraftRegColor,
              fontSize: 7,
              lineHeight: "11px",
              textAlign: "right",
            },
          },
          "&:hover": {
            backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.custom.timeline.contextListBackground})`,
            "& > div .aircraft_icons": {
              opacity: 1,
            },
            "& > div .aircraft_drag_handle": {
              opacity: 0.7,
            },
          },
          // Quick filter free mode (ENG-715): rows can be dragged into any vertical order.
          "&.reorderable > div": {
            cursor: "grab",
          },
          "&.dragging": {
            opacity: 0.35,
            "& > div": { cursor: "grabbing" },
          },
          // Selected aircraft are what the quick filter shows, so selection has to read at a glance (ENG-715).
          // The bar is an inset shadow rather than a border: it costs no layout width, and the group colour is set
          // inline on this element so a borderRight rule here would be overridden by it.
          // Selected rows carry a bright rail down the left edge and a tint that fades out towards the text, so a
          // column of them reads as a set of tagged rows rather than one solid slab of colour. The rail is drawn as
          // an inset shadow because the right hand border is already taken by the aircraft group colour, which
          // AircraftIdentityRow sets inline.
          "&.selected": {
            backgroundColor: "transparent",
            backgroundImage: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.34)}, ${alpha(
              theme.palette.primary.main,
              0.08
            )})`,
            boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`,
            "&:hover": {
              backgroundImage: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.46)}, ${alpha(
                theme.palette.primary.main,
                0.18
              )})`,
            },
            "& > div .aircraft_icons": {
              opacity: 1,
            },
            "& > div": {
              "& .aircraft_reg": {
                color: theme.custom.timeline.aircraftSelected,
                fontWeight: 500,
              },
              "& .aircraft_fleet": {
                color: alpha(theme.custom.timeline.aircraftSelected, 0.85),
              },
              "& .aircraft_type": {
                color: theme.custom.timeline.aircraftTypeColor,
              },
            },
          },
        },
      },
      // Maintenance Counters
      "& .aircraft_maintenance_counters": {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        width: 44,
        height: flightRowSettings.aircraftRowTotalHeight,
        borderBottom: "1px solid #1a1a1a",
        paddingRight: "2px",
        "& .mx_badge": {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          lineHeight: 1,
          "& .mx_badge_icon": {
            fontSize: Math.max(Math.round(flightRowSettings.aircraftRowTotalHeight * 0.26), 10),
          },
          "& .mx_badge_count": {
            fontSize: Math.max(Math.round(flightRowSettings.aircraftRowTotalHeight * 0.2), 8),
            fontWeight: 600,
            fontFamily: "Heebo, sans-serif",
            lineHeight: 1,
          },
        },
      },
      // Aircraft Live Status
      "& .aircraft_status": {
        width: 100,
        height: flightRowSettings.aircraftRowTotalHeight,
        borderBottom: "1px solid #1a1a1a",
      },
    },

    "& .timeline_aircraft_info_drawer": {
      userSelect: "none",
      pointerEvents: "none",
      width: 0, // Hide for now (disables feature)
      marginTop: "60px",
      backgroundColor: theme.custom.timeline.webglCanvas.selectedRow,
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      "& .timeline_aircraft_info_drawer_paper": {
        position: "relative",
        background: "transparent",
        border: "none",
        margin: "15px",
        "& .timeline_aircraft_info": { width: aircraftInfoWidth },
      },
      "&.open": {
        width: aircraftInfoWidth,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },

    "& .timeline_flights_column": {
      display: "flex",
      width: `calc(100% - ${aircraftColumnWidth}px)`,
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      position: "relative",
      backgroundColor: theme.palette.background.paper,
      "&.aicraftstatus_open": {
        width: `calc(100% - ${aircraftColumnWidth + aircraftStatusWidth}px)`,
      },
      "&.mx_counters_open": {
        width: `calc(100% - ${aircraftColumnWidth + maintenanceCountersWidth}px)`,
      },
      "&.aicraftstatus_open.mx_counters_open": {
        width: `calc(100% - ${aircraftColumnWidth + aircraftStatusWidth + maintenanceCountersWidth}px)`,
      },
      "&.aircraftinfo_open": {
        width: `calc(100% - ${aircraftColumnWidth + aircraftInfoWidth}px)`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
      "& .timeline_webgl_container": {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flexGrow: 1,
        "& .timeline_webgl_canvas": {
          position: "absolute",
          display: "flex",
          outline: "none",
        },
      },
    },
  },
});

const compareEvents = (hoveredEvent: IFlight | IGroundEvent) => (selectedEvent: IFlight | IGroundEvent) =>
  selectedEvent._id === hoveredEvent._id;

/**
 * A function that checks the input time to see if that interval's block of data is loaded
 * @param timeIntervalLoaded - the current flihgt data intervals loaded
 * @param timeToCheck - a time to check if its interval is already loaded
 */
export const checkIntervalBlockIsLoaded = (
  timeIntervalLoaded: ITimeIntervalLoaded,
  timeToCheck: moment.Moment
): {
  loaded: boolean;
  blockKey: string;
} => {
  const blockKey = `${timeToCheck.year()}-${timeToCheck.month()}`;
  const interval = timeIntervalLoaded[blockKey];
  return { loaded: interval ? true : false, blockKey: blockKey };
};

/**
 * Finds the ground event relevant to the position selected by the user
 * @param events - events
 * @param position -  Y position in the timeline
 * @param timelineGL - timelienGL object
 * @returns - event or null
 */

export const findEventAtPosition = (
  events: (IFlight | IGroundEvent)[],
  position: number,
  timelineGL: TimelineGL
): IFlight | IGroundEvent | null => {
  if (!events || events.length === 0) {
    return null;
  }

  // Find the time associated with the given position
  const time = Math.floor(timelineGL.pixelCoordsToEpochTime(position * window.devicePixelRatio)); // Convert position to time

  // Perform binary search to find the event
  let low = 0;
  let high = events.length - 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const event = events[mid];

    let startTime, endTime;

    // Determine start and end time of the event
    if (isFlight(event)) {
      startTime = new Date(event.actualOn).getTime();
      endTime = new Date(event.actualOff).getTime();
    } else {
      startTime = new Date(event.actualStart).getTime();
      endTime = new Date(event.actualEnd).getTime();
    }

    // Check if time falls within event timeframe
    if (time >= startTime && time <= endTime) {
      return event;
    } else if (time < startTime) {
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return null;
};

/**
 * Checks if there is any conflict with ground events before re-assigning the flights
 * @param groundEvents - ground events
 * @param reassignedEvents -  reassigned events
 * @returns - boolean conflict exists or not
 */
export const checkForConflict = (args: {
  groundEvents: IGroundEvent;
  reassignedEvents?: IFlight[];
  reassignedGroundEvents?: IGroundEvent[];
}): boolean => {
  let isConflict = false;
  const { groundEvents, reassignedEvents, reassignedGroundEvents } = args;
  if (reassignedEvents) {
    for (const event of reassignedEvents) {
      if (
        (event.actualOn <= groundEvents.actualStart && event.actualOff >= groundEvents.actualStart) ||
        (event.actualOn <= groundEvents.actualEnd && event.actualOff >= groundEvents.actualEnd) ||
        (event.actualOn >= groundEvents.actualStart && event.actualOff <= groundEvents.actualEnd)
      ) {
        isConflict = true;
      }
    }
  } else if (reassignedGroundEvents) {
    for (const event of reassignedGroundEvents) {
      if (
        (event.actualEnd <= groundEvents.actualStart && event.actualStart >= groundEvents.actualStart) ||
        (event.actualEnd <= groundEvents.actualEnd && event.actualStart >= groundEvents.actualEnd) ||
        (event.actualEnd >= groundEvents.actualStart && event.actualStart <= groundEvents.actualEnd)
      ) {
        isConflict = true;
      }
    }
  }
  return isConflict;
};

let isAnimating = false;
export const FlightTimeline = (props: IFlightTimelineProps): JSX.Element => {
  const { hideHoveredTimeIndicator, windowId, path, setIsActive } = props;
  const dispatch = useDispatch();
  const theme: Theme = useTheme();

  //user settings
  const userAppSettings: IUserAppSettings = useSelector((state: IRootState) => state.authentication.appSettings);
  const {
    blockInfo,
    flightTimelineViewScale,
    flightTimeline,
    highlightFlightSettings: rawHighlightFlightSettings,
  } = userAppSettings;
  const { followCurrentDate, currentDateOffsetHours } = flightTimeline;
  const { utc, icao } = blockInfo;
  const aircraftRowSetting = useSelector((state: IRootState) => state.timelineFlights.aircraftRowSetting);

  // Dispatch desk state — used to resolve desk flight IDs for highlighting
  const dispatchDesks: IDispatchDeskExpanded[] = useSelector((state: IRootState) => state.dispatchDesks.desks);

  // Enrich highlight settings with resolved dispatch desk flight IDs
  const highlightFlightSettings = useMemo(() => {
    if (!rawHighlightFlightSettings) return rawHighlightFlightSettings;
    const enriched = { ...rawHighlightFlightSettings };
    let deskDataChanged = false;

    // Resolve flight IDs for selected desks
    const selectedDeskIds = rawHighlightFlightSettings?.conditions?.dispatchDesk;
    if (selectedDeskIds?.length > 0) {
      const deskFlightIds = dispatchDesks
        .filter((desk) => selectedDeskIds.includes(desk._id))
        .flatMap((desk) => desk.flights.map((f) => f._id));
      enriched._deskFlightIds = deskFlightIds;
      deskDataChanged = true;
    }

    // Resolve all desk flight IDs for "not on any desk" highlighting
    if (rawHighlightFlightSettings?.conditions?.highlightUnassigned) {
      const allDeskFlightIds = dispatchDesks.flatMap((desk) => desk.flights.map((f) => f._id));
      enriched._allDeskFlightIds = allDeskFlightIds;
      deskDataChanged = true;
    }

    // When desk data changes and desk-based highlighting is active, bump lastApplied
    // so the GL buffers know to rebuild with the updated flight ID lists.
    if (deskDataChanged && enriched.active) {
      enriched.lastApplied = new Date().valueOf();
    }

    return enriched;
  }, [rawHighlightFlightSettings, dispatchDesks]);

  // Return highlight condition string (for use as tooltip in toggle icon) and active status.
  const { highlightIsActive, highlightConditions } = useMemo(() => {
    if (
      highlightFlightSettings?.conditions?.flightNumber?.length > 0 ||
      highlightFlightSettings?.conditions?.flightType?.length > 0 ||
      highlightFlightSettings?.conditions?.origin?.length > 0 ||
      highlightFlightSettings?.conditions?.destination?.length > 0 ||
      highlightFlightSettings?.conditions?.dispatchDesk?.length > 0 ||
      highlightFlightSettings?.conditions?.highlightUnassigned
    ) {
      const { conditions } = highlightFlightSettings;
      const { flightNumber, origin, destination, dispatchDesk, highlightUnassigned } = conditions;
      const flightNumberLabel = flightNumber?.length > 0 ? `Flight#: ${flightNumber}` : "";
      const originLabel = origin?.length > 0 ? `Origin: ${origin.toString()}` : "";
      const destinationLabel = destination?.length > 0 ? `Destination: ${destination.toString()}` : "";
      const deskLabel =
        dispatchDesk?.length > 0
          ? `Desk: ${dispatchDesks
              ?.filter((d) => dispatchDesk.includes(d._id))
              .map((d) => d.name)
              .join(", ")}`
          : "";
      const unassignedLabel = highlightUnassigned ? "Not On Any Desk" : "";
      return {
        highlightIsActive: highlightFlightSettings?.active,
        highlightConditions: [flightNumberLabel, originLabel, destinationLabel, deskLabel, unassignedLabel]
          .filter(Boolean)
          .join(" "),
      };
    }
    return {
      highlightIsActive: highlightFlightSettings?.active,
      highlightConditions: null,
    };
  }, [highlightFlightSettings, dispatchDesks]);

  /**
   * Toggle highlighted flights on and off. (doesn't remove settings, just changes active state without saving to db.)
   */
  const handleToggleHighlight = useCallback(() => {
    const newState = !highlightFlightSettings?.active;
    const updatedHighlightSettings = {
      ...highlightFlightSettings,
      active: newState,
      lastApplied: new Date()?.valueOf(),
    };
    const updatedAppSettings = { ...userAppSettings, highlightFlightSettings: updatedHighlightSettings };
    dispatch(updateUserAppSettings(updatedAppSettings, "highlightFlightSettings"));
  }, [highlightFlightSettings, userAppSettings, dispatch]);

  const [timelineIteration, setTimelineIteration] = useState<number>(1);
  const [rowPercentage, setRowPercentage] = useState<number>(aircraftRowSetting.rowHeightPercentage);

  const flightErrorsMap = useSelector((state: IRootState) => state.flightScanner.flightErrorsMap);
  const flights: IAircraftFlights = useSelector((state: IRootState) => state.timelineFlights.flightData);
  const flightDataLoading: string[] = useSelector((state: IRootState) => state.timelineFlights.flightDataLoading);

  const themeDefaults = useSelector(
    (state: IRootState) => state.structures.customerStructure.globalAppConfig.themeDefaults
  );

  const isCustomerStructuresLoaded: boolean = useSelector(
    (state: IRootState) => !!state.structures.customerStructure.globalAppConfig.appStage
  );

  const delayBufferDeparture = useSelector(
    (state: IRootState) => state.structures.customerStructure.globalAppConfig.customer.settings.delayBufferDeparture
  );

  const delayBufferArrival = useSelector(
    (state: IRootState) => state.structures.customerStructure.globalAppConfig.customer.settings.delayBufferArrival
  );

  const overdueDepartureMovementMsgMinutes = useSelector(
    (state: IRootState) =>
      state.structures.customerStructure.globalAppConfig.customer.settings.overdueDepartureMovementMsgMinutes
  );

  const overdueArrivalMovementMsgMinutes = useSelector(
    (state: IRootState) =>
      state.structures.customerStructure.globalAppConfig.customer.settings.overdueArrivalMovementMsgMinutes
  );

  const [viewScale, setViewScale] = useState<string>(flightTimelineViewScale);

  // Custom Timeline settings
  const timelineSettings = useMemo(
    () => ({
      delayBufferDeparture,
      delayBufferArrival,
      icao,
      utc,
      currentDateOffsetHours: followCurrentDate ? currentDateOffsetHours : 0,
      viewScale,
      overdueDepartureMovementMsgMinutes,
      overdueArrivalMovementMsgMinutes,
    }),
    [
      delayBufferDeparture,
      delayBufferArrival,
      icao,
      utc,
      followCurrentDate,
      currentDateOffsetHours,
      viewScale,
      overdueDepartureMovementMsgMinutes,
      overdueArrivalMovementMsgMinutes,
    ]
  );

  // AIRCRAFT FILTERING AND SORTING
  const allAircraft = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  const { aircraftFilters, aircraftSort } = useSelector((state: IRootState) => state.timelineFlights);
  const aircraftGroups = useSelector((state: IRootState) => state.structures.customerStructure.aircraftGroups);

  //
  const aircraftFiltersOptions = useMemo(() => {
    return convertSelectedFiltersToFilterOptions(aircraftFilters, allAircraft, aircraftGroups);
  }, [allAircraft, aircraftGroups, aircraftFilters]);
  // Apply any actively selected filters or return all aircraft.
  const baseFilteredAircraft = useMemo(() => {
    const filtered =
      allAircraft?.length > 0 && aircraftFiltersOptions?.length > 0
        ? applyAircraftFilter(allAircraft, aircraftFiltersOptions)
        : allAircraft;
    return aircraftSort ? sortFilteredAircraft(filtered, aircraftSort) : filtered;
  }, [allAircraft, aircraftSort, aircraftFiltersOptions]);

  // Aircraft selected in this window's aircraft column. Drives the row highlight, bulk assignment and the quick
  // filter. Per window, so a second flight display open beside this one keeps its own selection.
  const selectedAircraftArray =
    useSelector((state: IRootState) => state.timelineFlights.selectedAircraft?.[windowId]) ?? noAircraftSelected;

  // AIRCRAFT QUICK FILTER (ENG-715)
  // Temporary, in-memory only, and held per flight display window (like interactiveState) so each mosaic window
  // filters independently. The filter is pinned to a snapshot of the aircraft that were selected when the user
  // applied it, not to the live selection: while it is applied, clicking a chip still selects and deselects it
  // (for highlighting and bulk assignment) without rows disappearing from under the user. Re-applying re-snapshots.
  const quickFilter =
    useSelector((state: IRootState) => state.timelineFlights.quickFilters?.[windowId]) ?? defaultAircraftQuickFilter;
  const quickFilterAircraft = quickFilter.aircraft;
  const quickFilterActive = (quickFilterAircraft?.length ?? 0) > 0;
  // Custom vertical order set by dragging rows while the quick filter is applied ("free mode"). null = normal sort.
  const quickFilterOrder = quickFilter.order;
  //! Important, this is the list every GL consumer below draws from - the quick filter must be applied here.
  const filteredAircraft = useMemo(
    () => applyQuickAircraftFilter(baseFilteredAircraft, quickFilterAircraft, quickFilterOrder),
    [baseFilteredAircraft, quickFilterAircraft, quickFilterOrder]
  );
  // The rows on screen, and the selected ones among them. Applying the filter pins it to the selected ids, so both
  // are measured against what is actually displayed rather than against aircraft the aircraft filter menu hides.
  const displayedAircraftIds = useMemo(
    () => filteredAircraft?.map((ac: IAircraft) => ac._id as string) ?? [],
    [filteredAircraft]
  );
  const selectedAircraftIds = useMemo(
    () => selectedAircraftIdsIn(filteredAircraft, selectedAircraftArray),
    [filteredAircraft, selectedAircraftArray]
  );

  const interactiveState = useSelector((state: IRootState) => state.timelineFlights.interactiveState[windowId]);
  const flightFlags = useSelector((state: IRootState) => state.structures.customerStructure.flightFlags);

  const [startBuffer, endBuffer] = [345600000 / 2, 345600000 / 2]; // default 4 days
  const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);
  const hoveredEvent = useSelector((state: IRootState) => state.timelineFlights.hoveredFlight);
  const focusedEventsObj = useSelector((state: IRootState) => state.timelineFlights.focusedEvents);
  const { shouldFocus } = focusedEventsObj;
  const focusedEvent = focusedEventsObj.events;
  const crewConnections = useSelector((state: IRootState) => state.timelineFlights.crewConnections[windowId]);
  const selectedDay = useSelector((state: IRootState) => state.timelineFlights.selectedDay?.[windowId]);
  const timelineGLs = useSelector((state: IRootState) => state.timelineFlights.timelineGLs);

  const selectedAircraft = selectedAircraftArray?.[0];
  const [timelineGL, setTimelineGL] = useState<TimelineGL>();
  const [captureMode, setCaptureMode] = useState<boolean>(false);
  const [pointerId, setPointerId] = useState<number>(0);
  //If you are shift selecting timeline events, keep track of what events were the "anchor" event for that aircraft
  const [anchorSelectedEvents, setAnchorSelectedEvents] = useState<{
    [key: string]: {
      event: IFlight | IGroundEvent;
    };
  }>({});

  const timelineContainer = useRef<HTMLDivElement>(null);
  const mount = useRef<HTMLDivElement>(null);
  const frameIdRef = useRef<number>(0);
  const currentTouchEvent = useRef<React.Touch>(null);
  const aircraftView = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<number>(Date.now());
  const lastUpdatedTime = useRef<number>(Date.now());
  const canUpdateHoveredFlightRef = useRef<boolean>(true);
  const canUpdateReassignedFlight = useRef<boolean>(true);
  const shouldDeselectRef = useRef<boolean>(true);

  const [showTimelineDatePickers, setShowTimelineDatePickers] = useState<boolean>(false);

  //flight display label settings
  const [isFlightDisplayUTC, setIsFlightDisplayUTC] = useState<boolean>(utc);
  const [isFlightDisplayICAO, setIsFlightDisplayICAO] = useState<boolean>(icao);

  const currentDate = useMemo(() => new Date(), []);
  // Compute the window with epoch math (now +/- N ms). The previous approach mixed
  // getUTCDate() (UTC day-of-month) with setDate() (local month), which skewed the
  // window to the 1st of the local month at month boundaries once local time passed
  // midnight UTC. Timestamp arithmetic avoids both the UTC/local mismatch and month
  // overflow. Do not revert to setDate/getUTCDate.
  const startDate = useMemo(() => new Date(currentDate.getTime() - 15 * 86400000), [currentDate]);
  const endDate = useMemo(() => new Date(currentDate.getTime() + 15 * 86400000), [currentDate]);

  // setUserSetScale is called "once", a second after user has moved timeline with zoom, mouse drag or touch drag.
  const debouncedSetScale = debounce((): any => {
    if (timelineGL && dispatch) dispatch(setUserSetScale(timelineGL.getTimeRange()));
  }, 1000);

  //live loading
  const timeIntervalLoaded: ITimeIntervalLoaded = useSelector(
    (state: IRootState) => state.timelineFlights.timeIntervalLoaded
  );

  // New Code added to display aircraft information popup on click of registration. -CC
  // const [aircraftInfoAnchor, setAircraftInfoAnchor] = React.useState<any | null>(null);

  const dataLoadedCheck = useCallback(
    (minTime: number, maxTime: number): void => {
      dispatch(retrieveFlightData(new Date(minTime), new Date(maxTime)));
    },
    [dispatch]
  );

  /**
   * for helping with capturing the scroll bar drag when mouse is outside the window
   */
  const exitCaptureMode = useCallback((pointerId: number): void => {
    if (pointerId) mount?.current?.releasePointerCapture(pointerId);
    setCaptureMode(false);
    setPointerId(0);
  }, []);

  const enterCaptureMode = useCallback((pointerId: number): void => {
    setPointerId(pointerId);
    mount?.current?.setPointerCapture(pointerId);
    setCaptureMode(true);
  }, []);

  /**
   * Left clicking empty space in the flight display closes the flight context menu and leaves bulk assignment mode.
   * It deliberately leaves the aircraft selection alone (ENG-715): a stray click in the timeline should not throw
   * away a list of aircraft the user picked out by hand, and the toolbar has a control for clearing it on purpose.
   */
  const handleCloseContext = useCallback(() => {
    dispatch(setSelectedDay(null, windowId));
    dispatch(setIsFlightContextOpen(false, windowId));
  }, [dispatch, windowId]);

  /**
   * Highlight selected aircraft row and modify/toggle bulk assignment mode.
   * anchor is used for eventual integration of flight tracker data.
   * @param registration -
   * @param anchor -
   */
  const showAircraftInfo = useCallback(
    (id: string, event: React.MouseEvent): void => {
      const selection = selectedAircraftArray ?? [];
      const anchorId = lastAircraftClickRef.current;
      lastAircraftClickRef.current = id;

      // Shift+click extends the selection from the last clicked aircraft to this one, so a block of aircraft can be
      // picked for the quick filter in two clicks.
      const anchorIndex = anchorId ? filteredAircraft.findIndex((ac: IAircraft) => ac._id === anchorId) : -1;
      const targetIndex = filteredAircraft.findIndex((ac: IAircraft) => ac._id === id);
      if (event?.shiftKey && anchorIndex >= 0 && targetIndex >= 0 && anchorIndex !== targetIndex) {
        const shouldSelect = !selection.includes(id);
        const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
        const rangeIds = filteredAircraft.slice(from, to + 1).map((ac: IAircraft) => ac._id as string);
        dispatch(
          setSelectedAircraft(
            shouldSelect
              ? [...selection, ...rangeIds.filter((rangeId: string) => !selection.includes(rangeId))]
              : selection.filter((selectedId: string) => !rangeIds.includes(selectedId)),
            windowId
          )
        );
        return;
      }

      const foundId: string = selectedAircraftArray?.find((selected: string) => selected === id);
      if (selectedAircraftArray && !foundId) {
        //add selected aircraft
        dispatch(setSelectedAircraft([...[id], ...selectedAircraftArray], windowId));
      } else if (foundId && !!selectedDay) {
        // cancel bulk assignment mode if same aircraft is clicked.
        dispatch(setBulkAssignmentMode(null, [], windowId));
        dispatch(setIsFlightContextOpen(false, windowId));
      } else {
        //remove from array
        const index: number = selectedAircraftArray?.indexOf(id);
        if (index > -1) {
          const temp = [...selectedAircraftArray];
          temp.splice(index, 1);
          dispatch(setSelectedAircraft(temp, windowId));
        }
      }
    },
    [selectedAircraftArray, selectedDay, dispatch, windowId, filteredAircraft]
  );

  /**
   * Opens the aircraft context panel, optionally focused on a specific tab.
   * Defaults to the "overview" tab when no tab key is provided (e.g. right-click).
   */
  const showAircraftContextPanel = useCallback(
    (aircraftId: string, event: React.MouseEvent, tabKey?: string): void => {
      event.preventDefault();
      event.stopPropagation();
      // Close the flight context menu if open
      dispatch(setIsFlightContextOpen(false, windowId));
      // Open the aircraft context panel
      dispatch(setAircraftContextId(aircraftId, windowId, tabKey));
      dispatch(setIsAircraftContextOpen(true, windowId));
    },
    [dispatch, windowId]
  );

  // Remembers the last aircraft clicked so shift+click can select a range of rows.
  const lastAircraftClickRef = useRef<string | null>(null);

  /**
   * Toolbar select-all control. Branches on the selected aircraft that are on screen, the same count the checkbox
   * shows: clears the selection when any of them is selected, otherwise selects every visible row. Aircraft that are
   * selected but hidden by the aircraft filter are dropped either way, so a click never just clears a selection the
   * user cannot see.
   */
  const handleSelectAllAircraft = useCallback((): void => {
    lastAircraftClickRef.current = null;
    if (selectedAircraftIds.length > 0) {
      dispatch(setSelectedAircraft([], windowId));
    } else {
      dispatch(setSelectedAircraft(displayedAircraftIds, windowId));
    }
  }, [dispatch, selectedAircraftIds, displayedAircraftIds, windowId]);

  /**
   * Quick filter "free mode" drag reorder: move the dragged aircraft to the hovered aircraft's position. The full
   * displayed order is stored so the arrangement survives Reset / re-apply; the GL rows follow the same list.
   */
  const handleQuickFilterMove = useCallback(
    (fromAircraftId: string, toAircraftId: string): void => {
      if (!quickFilterActive) return;
      const next = moveAircraftInOrder(displayedAircraftIds, fromAircraftId, toAircraftId);
      if (next !== displayedAircraftIds) dispatch(setQuickFilterOrder(next, windowId));
    },
    [dispatch, displayedAircraftIds, quickFilterActive, windowId]
  );

  // Auto-scroll the timeline while a row is dragged near the top or bottom edge of the aircraft column. The column
  // itself is overflow hidden and scroll-synced to the GL camera, so the camera is what has to move.
  const lastDragScrollRef = useRef<number>(0);
  const handleQuickFilterDragHover = useCallback(
    (clientY: number): void => {
      if (!timelineGL || !aircraftView.current) return;
      const now = Date.now();
      if (now - lastDragScrollRef.current < 40) return;
      const { top, bottom } = aircraftView.current.getBoundingClientRect();
      const edge = Math.max(aircraftRowSetting.aircraftRowTotalHeight, 24);
      const step = Math.max(Math.round(aircraftRowSetting.aircraftRowTotalHeight / 4), 6);
      if (clientY < top + edge) {
        timelineGL.moveCameraByYPixels(-step);
      } else if (clientY > bottom - edge) {
        timelineGL.moveCameraByYPixels(step);
      } else {
        return;
      }
      lastDragScrollRef.current = now;
    },
    [timelineGL, aircraftRowSetting]
  );

  const [showAircraftStatus, setShowAircraftStatus] = useState<boolean>(false);
  const [showMaintenanceCounters, setShowMaintenanceCounters] = useState<boolean>(false);

  const [{ isOver }, drop] = useDrop({
    accept: ["UNASSIGNED_FLIGHT", "UNASSIGNED_FLIGHTS"],
    drop(item: any, monitor: DropTargetMonitor) {
      //const dragItem: any = monitor.getItem(); not sure why this was here? - CC
      const items = item?.flights ?? [item?.flight];
      const flights = cloneDeep(items);
      const yPos = monitor?.getClientOffset()?.y - mount?.current?.getBoundingClientRect()?.top;
      saveReassignedEvents(flights, yPos);
    },
    hover(item: any, monitor: DropTargetMonitor) {
      const yPos = monitor?.getClientOffset()?.y - mount?.current?.getBoundingClientRect()?.top;
      const flights = item?.flights ?? [item?.flight];
      updateReassignedEvents(flights, yPos);
    },
    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  const scrollAircraftView = useCallback(() => {
    const [yMin] = timelineGL.getYRange();
    if (aircraftView.current) {
      aircraftView.current.scrollTop = yMin;
    }
  }, [aircraftView, timelineGL]);

  //! Important, logic in timeline is applied to all aircraft but only draw filtered list.
  const animate = useCallback(() => {
    if (timelineGL) {
      timelineGL.draw(
        filteredAircraft,
        { selectedDay, selectedAircraft },
        selectedAircraftArray,
        highlightFlightSettings
      );
      scrollAircraftView();
      if (isAnimating) {
        frameIdRef.current = window.requestAnimationFrame(animate);
      }
    }
  }, [
    timelineGL,
    filteredAircraft,
    scrollAircraftView,
    selectedAircraft,
    selectedDay,
    selectedAircraftArray,
    highlightFlightSettings,
  ]);

  //if you change the UTC or the IATA then redraw the timeline...
  const toggleICAO = useCallback(
    (value: boolean): void => {
      if (timelineGL) {
        timelineGL?.setIsICAO(value);
        setIsFlightDisplayICAO(value);
        dispatch(
          updateUserAppSettings(
            { ...userAppSettings, blockInfo: { ...userAppSettings.blockInfo, ...{ icao: value } } },
            "blockInfo"
          )
        );
        if (!isAnimating) {
          animate();
        }
      }
    },
    [animate, timelineGL, dispatch, userAppSettings]
  );

  const toggleUTC = useCallback(
    (value: boolean): void => {
      if (timelineGL) {
        timelineGL?.setIsUTC(value);
        setIsFlightDisplayUTC(value);
        dispatch(
          updateUserAppSettings(
            { ...userAppSettings, blockInfo: { ...userAppSettings.blockInfo, ...{ utc: value } } },
            "blockInfo"
          )
        );
        if (!isAnimating) {
          animate();
        }
      }
    },
    [animate, timelineGL, dispatch, userAppSettings]
  );

  useEffect(() => {
    if (timelineGL) {
      timelineGL.updateFlightFlags(flightFlags);
    }
  }, [flightFlags, timelineGL]);

  useEffect(() => {
    if (timelineGL) {
      timelineGL.setTheme(theme.custom.timeline.webglCanvas);
    }
  }, [timelineGL, theme]);

  const updateReassignedEvents = (events: (IFlight | IGroundEvent)[], yPos: number): void => {
    if (!canUpdateReassignedFlight.current) {
      return;
    }
    canUpdateReassignedFlight.current = false;

    const aircraftIndex = timelineGL.getAircraftFromYPixel(yPos);
    setTimeout(() => (canUpdateReassignedFlight.current = true), 50);

    if (events.length === 0 || aircraftIndex < 0 || aircraftIndex >= filteredAircraft.length) {
      timelineGL.setReassignedFlights([]);
      return;
    }
    const aircraft = filteredAircraft[aircraftIndex];
    let avgMid = 0;
    const reassignedEvents = events.map((event: IFlight | IGroundEvent) => {
      const departure = isFlight(event) ? moment(event.actualOff)?.valueOf() : moment(event.actualStart)?.valueOf();
      const arrival = isFlight(event) ? moment(event.actualOn)?.valueOf() : moment(event.actualEnd)?.valueOf();
      avgMid += departure + (arrival - departure) / 2.0;
      return {
        ...event,
        aircraft: {
          ...aircraft,
        },
      } as IFlight | IGroundEvent;
    });

    const [xmin, xmax] = timelineGL.getTimeRange();
    const delta = (xmax - xmin) / 2.0;
    const mid = avgMid / events.length;
    const [newXMin, newXMax] = [mid - delta, mid + delta];
    timelineGL.setTimeAxis(moment(newXMin)?.valueOf(), moment(newXMax)?.valueOf());
    timelineGL.setReassignedFlights(reassignedEvents);
    if (!isAnimating) {
      animate();
    }
  };

  const saveReassignedEvents = (events: (IFlight | IGroundEvent)[], yPos: number): void => {
    const aircraftIndex = timelineGL.getAircraftFromYPixel(yPos);
    if (aircraftIndex < 0 || aircraftIndex >= filteredAircraft.length) {
      return;
    }
    const flightAircraft = filteredAircraft[aircraftIndex]?._id
      ? convertAircraftToFlightAircraft(filteredAircraft[aircraftIndex])
      : null;

    events.forEach((event: IFlight | IGroundEvent) => {
      if (event.aircraft?._id === flightAircraft?._id) {
        //dont want to make a call to reassign a flight to its existing AC
        return;
      }
      event.aircraft = flightAircraft;
      if (isFlight(event)) {
        const payload = {
          ...{ aircraft: flightAircraft, aircraftConfiguration: filteredAircraft?.[aircraftIndex]?.defaultConfig },
          ...{ updateType: "ReassignAircraft" },
        };
        dispatch(updateSelectedFlight(event._id, payload));
      } else {
        dispatch(updateSelectedGroundEvent(event._id, { aircraft: flightAircraft }));
      }
    });

    timelineGL.setReassignedFlights([]);
    if (!isAnimating) {
      animate();
    }
  };

  useEffect(() => {
    if (interactiveState === InteractiveState.Reassign) {
      // Debug.log("REASSIGNING")
      timelineGL.setReassignedFlights([...selectedEvents]);
    }
    // If any operation clears all selected events and timeline is locked, unlock it.
    if (selectedEvents?.length === 0 && interactiveState === InteractiveState.LockTimeline) {
      dispatch(setInteractiveState(InteractiveState.Pan, windowId));
    }
  }, [interactiveState, timelineGL, selectedEvents, dispatch, windowId]);

  useEffect(() => {
    if (timelineGL && !isOver) {
      timelineGL.setReassignedFlights([]);
    }
  }, [timelineGL, isOver]);

  const handleTouchDown = (event: React.TouchEvent): void => {
    currentTouchEvent.current = event.touches[0];

    if (!timelineGL) {
      return;
    }

    const hoveredEvent = timelineGL.getHoveredEvent();
    if (hoveredEvent && selectedEvents.findIndex(compareEvents(hoveredEvent)) < 0) {
      updateSelectedEvents(event, hoveredEvent);
    }

    isAnimating = true;
    start();
  };
  const handleTouchMove = (event: React.TouchEvent): void => {
    if (!timelineGL || interactiveState === InteractiveState.LockTimeline) {
      return;
    }

    const downTouch = currentTouchEvent.current;
    const touch = event.touches[0];
    if (!touch || !downTouch) {
      return;
    }

    const deltaX = touch.clientX - currentTouchEvent?.current?.clientX;
    const deltaY = touch.clientY - currentTouchEvent?.current?.clientY;

    // handle moving camera
    if (isAnimating) {
      timelineGL.moveCameraByXPixels(deltaX);
      timelineGL.moveCameraByYPixels(deltaY);
      // Set user assigned scale when camera is moved by user DRAGGING touch.
      debouncedSetScale();
    } else if (canUpdateHoveredFlightRef.current) {
      canUpdateHoveredFlightRef.current = false;
      updateHoveredFlight(touch.clientX, touch.clientY);
      setTimeout(() => (canUpdateHoveredFlightRef.current = true), 33);
    }
    currentTouchEvent.current = touch;
  };

  const handleTouchUp = (event: React.TouchEvent): void => {
    currentTouchEvent.current = null;
    isAnimating = false;
  };
  /**
   * Conflict Warning Modal
   * @param selectedEvents - selected events by the user.
   * @param yPos - Y pos in timeline.
   */
  const displayWarningModal = (selectedEvents: (IFlight | IGroundEvent)[], yPos: number): void => {
    dispatch(
      openModal({
        root: {
          data: {
            title: "Reassigned Flight Overlap",
            message:
              "Reassigning the selected flight(s) will result in an overlap with an existing ground event. Do you want to continue?",
            buttons: [
              {
                label: "Cancel",
                handler: () => {
                  setInteractiveState(InteractiveState.None, windowId);
                  setShowAircraftStatus(false);
                  dispatch(setSelectedEvents([]));
                  dispatch(closeModal({ root: { data: null } })); // Close the modal
                },
              },
              {
                label: "Continue",
                handler: () => {
                  saveReassignedEvents(selectedEvents, yPos);
                  dispatch(closeModal({ root: { data: null } }));
                },
              },
            ],
          },
        },
      })
    );
  };

  let clickedOnScroll: ScrollBarClick = 1;
  // let interactionMode = InteractiveState.None;
  const handleMouseDown = (event: React.MouseEvent): void => {
    if (event.button !== 0 || !timelineGL || interactiveState === InteractiveState.LockTimeline) {
      return;
    }

    const rect = mount?.current?.getBoundingClientRect();
    const scaledX = (event.clientX - rect.left) * window.devicePixelRatio;
    const yPos = event.clientY - mount?.current?.getBoundingClientRect()?.top;

    if (yPos > 0 && yPos < 60) {
      // If user clicks on top 60 pixels of the timeline (the time axis), display the date range pickers.
      setShowTimelineDatePickers(true);
    }

    if (interactiveState === InteractiveState.Reassign) {
      const aircraftInfo = timelineGL.getAircraftIfOnEventTarget(yPos);
      const aircraftIndex = aircraftInfo.aircraftIndex;
      const aircraft = filteredAircraft[aircraftIndex];
      const aircraftEvents = flights[aircraft._id];

      const events = aircraftEvents?.groundEvents
        ? findEventAtPosition(aircraftEvents.groundEvents, scaledX, timelineGL)
        : null;

      if (events) {
        const groundEvent = events as IGroundEvent;
        const selectedFlightEvents = selectedEvents as IFlight[];
        //Check for the conflict
        const isConflict = checkForConflict({ groundEvents: groundEvent, reassignedEvents: selectedFlightEvents });

        if (isConflict) {
          if (userAppSettings?.warnTailSwap) {
            displayWarningModal(selectedEvents, yPos);
          } else {
            saveReassignedEvents(selectedEvents, yPos);
          }
          timelineGL.setReassignedFlights([]);
        }
      } else {
        saveReassignedEvents(selectedEvents, yPos);
        timelineGL.setReassignedFlights([]);
      }
    }

    clickedOnScroll = timelineGL.scrollClick(
      (event.clientX - mount?.current?.getBoundingClientRect()?.left) * window.devicePixelRatio,
      event.clientY - mount?.current?.getBoundingClientRect()?.top - timelineGL.timelineStart
    );

    if (clickedOnScroll === ScrollBarClick.None) {
      dispatch(setInteractiveState(InteractiveState.Pan, windowId));
    } else if (clickedOnScroll === ScrollBarClick.ClickedOnThumb) {
      dispatch(setInteractiveState(InteractiveState.ScrollBarThumb, windowId));
    } else {
      dispatch(setInteractiveState(InteractiveState.ScrollBar, windowId));
    }
    shouldDeselectRef.current = true;
    isAnimating = true;
    start();
  };

  // SHIFT/CTRL Event selection handler.
  const updateSelectedEvents = useCallback(
    (event: React.TouchEvent | React.MouseEvent, newSelectedEvent: IFlight | IGroundEvent): void => {
      const { ctrlKey, metaKey, shiftKey } = event;
      const multiSelectKey = ctrlKey || metaKey;
      // Append If selected events are matching type, otherwise create new selection array.
      const selectionType = isFlight(newSelectedEvent) ? "flights" : "groundEvents";
      const sameEventType: boolean =
        selectedEvents.length === 0 ||
        (selectedEvents.length > 0 && isFlight(newSelectedEvent) === isFlight(selectedEvents[0]));
      const alreadySelected = !!selectedEvents.find((event) => event._id === newSelectedEvent._id);
      if (multiSelectKey && !shiftKey) {
        // SELECT INDIVIDUAL EVENT
        const newSelections = sameEventType
          ? alreadySelected //is the newSelectedEvent already selected? if so, remove it
            ? selectedEvents.filter((event) => event._id !== newSelectedEvent._id)
            : [...selectedEvents, newSelectedEvent]
          : [newSelectedEvent];
        dispatch(setSelectedEvents(newSelections));
        setAnchorSelectedEvents((prev) => ({
          ...prev,
          ...{ [newSelectedEvent.aircraft?._id]: { event: newSelectedEvent } },
        }));
      } else if (!multiSelectKey && shiftKey) {
        // SELECT RANGE OF EVENTS
        const selectedEventAircraft = selectedEvents.map((event: IFlight | IGroundEvent) => event?.aircraft?._id);
        selectedEventAircraft.push(newSelectedEvent?.aircraft?._id);
        // Remove duplicates
        const uniqueSelectedAircraft = Array.from(new Set(selectedEventAircraft));
        let allACEventRange: (IFlight | IGroundEvent)[] = [];
        uniqueSelectedAircraft.forEach((id: string) => {
          const aircraftEvents = flights[id][selectionType];
          const newSelections = [...selectedEvents, newSelectedEvent];
          const ACSelections = newSelections.filter((event: IFlight | IGroundEvent) => event?.aircraft?._id === id);
          //if the newly selected event ISN"T on this aircraft than don't modify the events for this AC
          if (id !== newSelectedEvent?.aircraft?._id) {
            allACEventRange = [...allACEventRange, ...ACSelections];
            return; //go to the next aircraft in the forEach loop ( return is same as continue with forEach)
          }

          // find earliest departure and latest arrival in new selections.
          const timeKey = selectionType === "flights" ? "actualOff" : "actualStart";
          let earliestDep = ACSelections.reduce((eventA: IFlight | IGroundEvent, eventB: IFlight | IGroundEvent) =>
            ///@ts-ignore  requires rewrite of the timeline data.
            new Date(eventA[timeKey]) < new Date(eventB[timeKey]) ? eventA : eventB
          );
          let latestDep = ACSelections.reduce((eventA: IFlight | IGroundEvent, eventB: IFlight | IGroundEvent) =>
            ///@ts-ignore  requires rewrite of the timeline data.
            new Date(eventA[timeKey]) > new Date(eventB[timeKey]) ? eventA : eventB
          );

          //what is the anchor flight for this AC
          const anchorEvent =
            newSelectedEvent?.aircraft?._id === id && anchorSelectedEvents[newSelectedEvent?.aircraft?._id]?.event;
          ///@ts-ignore  requires rewrite of the timeline data.
          if (anchorEvent?.[timeKey] > newSelectedEvent[timeKey]) {
            earliestDep = newSelectedEvent;
            latestDep = anchorEvent;
          } else if (anchorEvent) {
            earliestDep = anchorEvent;
            latestDep = newSelectedEvent;
          } else {
            //if there isn't an anchor event for this ac, add one
            setAnchorSelectedEvents((prev) => ({
              ...prev,
              ...{ [newSelectedEvent.aircraft?._id]: { event: newSelectedEvent } },
            }));
          }

          // Select all events within unique aircraft range.
          const selectedACEvents =
            selectionType === "flights"
              ? (aircraftEvents as IFlight[]).filter(
                  (event: IFlight) =>
                    event.actualOff >= (earliestDep as IFlight).actualOff &&
                    event.actualOff <= (latestDep as IFlight).actualOff
                )
              : (aircraftEvents as IGroundEvent[]).filter(
                  (event: IGroundEvent) =>
                    event.actualStart >= (earliestDep as IGroundEvent).actualStart &&
                    event.actualStart <= (latestDep as IGroundEvent).actualStart
                );
          // Append to current selected range.
          allACEventRange = [...allACEventRange, ...selectedACEvents];
        });
        // Once all selected event ranged on all unique aircraft are calculated, setSelectedEvents.
        dispatch(setSelectedEvents(allACEventRange));
      } else {
        if (alreadySelected && selectedEvents.length === 1) {
          //This is where a user deselects a timeline event
          dispatch(setSelectedEvents([]));
          setAnchorSelectedEvents({});
        } else {
          dispatch(setSelectedEvents([newSelectedEvent]));
          setAnchorSelectedEvents({ [newSelectedEvent.aircraft?._id]: { event: newSelectedEvent } });
        }
      }
    },
    [anchorSelectedEvents, dispatch, flights, selectedEvents]
  );

  const stopMouseInteraction = (event: React.MouseEvent): void => {
    shouldDeselectRef.current = true;
    event.preventDefault();
    if (interactiveState !== InteractiveState.Pan) {
      dispatch(setInteractiveState(InteractiveState.Pan, windowId));
    }

    isAnimating = false;
  };

  const handleMouseLeave = (event: any): void => {
    if (!timelineGL) {
      return;
    }
    //if you leave the window - clear hovered event
    timelineGL.setHoveredEvent(null);
    dispatch(setHoveredEvent(null));

    if (interactiveState !== InteractiveState.ScrollBarThumb) timelineGL.hoveredOnScrollBarThumb(0, 0);

    event.preventDefault();

    switch (interactiveState) {
      case InteractiveState.LockTimeline:
        break;
      case InteractiveState.ScrollBarThumb:
        if (!mount) {
          console.log("no timelineContainerElement exists");
          return;
        } else if (!captureMode || !pointerId) {
          enterCaptureMode(event.pointerId);
        }
        break;
      case InteractiveState.Reassign:
      case InteractiveState.BulkReassign:
        break;
      default:
        stopMouseInteraction(event);
        break;
    }
  };

  useEffect(() => {
    const cancelWheel = (event: any): void => event.preventDefault();
    timelineContainer.current.addEventListener("wheel", cancelWheel, { passive: false });
    return () => {
      timelineContainer?.current?.removeEventListener("wheel", cancelWheel);
    };
  }, []);

  /**
   * Find ground time interval by looking at 2 adjacent flights on timeline.
   */
  const setGroundTimeInterval = useCallback(
    (clientX: number, clientY: number): void => {
      //check for events in this aircraft row before/after your mouse pointer
      const [minTime] = timelineGL.getTimeRange();
      const rect = mount?.current?.getBoundingClientRect();
      const scaledX = (clientX - rect.left) * window.devicePixelRatio;
      const xTime = new Date(minTime + timelineGL.pixelCoordsToTimeAxis(scaledX));
      const y = clientY - rect.top;
      const aircraftInfo = timelineGL.getAircraftIfOnEventTarget(y);
      const aircraft = filteredAircraft[aircraftInfo.aircraftIndex];
      if (aircraft?._id) {
        const interval = timelineGL.getPreviousNextEvents(xTime.getTime(), aircraft._id);
        dispatch(setHoveredGroundTimeInterval(interval));
      }
    },
    [dispatch, filteredAircraft, timelineGL]
  );

  const handleMouseUp = (mouseEvent: React.MouseEvent): void => {
    mouseEvent.preventDefault();
    if (interactiveState === InteractiveState.LockTimeline) {
      return;
    }

    //check if where the mouse has ended is in the month(s) of loaded Data
    const [minTime, maxTime] = timelineGL.getTimeRange();
    if (dataLoadedCheck && typeof dataLoadedCheck === "function") {
      dataLoadedCheck(minTime, maxTime);
    }

    //to return the mouse thumb color to default
    timelineGL.hoveredOnScrollBarThumb(
      (mouseEvent.clientX - mount.current.getBoundingClientRect().left) * window.devicePixelRatio,
      mouseEvent.clientY - mount.current.getBoundingClientRect().top - timelineGL.timelineStart
    );

    if (shouldDeselectRef.current && interactiveState === InteractiveState.Pan) {
      const hoveredEvent = timelineGL.getHoveredEvent();

      if (hoveredEvent) {
        // If user has clicked on a valid event, clear the ground time interval.
        dispatch(setHoveredGroundTimeInterval({ prevEvent: undefined, nextEvent: undefined }));
        // If user has clicked on an event while in bulk assignment mode, cancel it. The aircraft selection stays
        // as it is, for the same reason as handleCloseContext above.
        if (!!selectedDay && !!selectedAircraft) {
          dispatch(setSelectedDay(null, windowId));
        }
      } else if (mouseEvent.button === 0) {
        // If user has clicked over an empty spot in the timeline, set ground time interval and clear hovered event in redux.
        setGroundTimeInterval(mouseEvent.clientX, mouseEvent.clientY);
      }

      // Allow selection of event with both left and right click.
      if (!!hoveredEvent && (mouseEvent.button === 0 || mouseEvent.button === 2)) {
        //Did you right Click on a NON-Selected Event?
        if (selectedEvents.findIndex(compareEvents(hoveredEvent)) < 0 && mouseEvent.button === 2) {
          //Yes, I did
          //Do I care if you had CRTL or SHIFT down? file explorer doesn't 🙄
          dispatch(setSelectedEvents([hoveredEvent]));
          setAnchorSelectedEvents({ [hoveredEvent.aircraft?._id]: { event: hoveredEvent } });
        } else if (mouseEvent.button === 2) {
          //dont deselect this already selected event
          // this is the click to open the context menu
        } else {
          //you have clicked left clicked on an event
          //updateSelectedEvents will check if you are holding ctrl or shift
          //and if you are clicking an already selected event or not
          updateSelectedEvents(mouseEvent, hoveredEvent);
        }
      } else if (!hoveredEvent) {
        dispatch(setSelectedEvents([]));
        if (mouseEvent.button === 0) handleCloseContext();
      }
    }
    //if you are reassigning, do not stop the mouse interaction
    if (interactiveState !== InteractiveState.Reassign) stopMouseInteraction(mouseEvent);
  };

  const handleMouseMove = (event: React.MouseEvent): void => {
    if (!timelineGL || interactiveState === InteractiveState.LockTimeline) {
      return;
    }

    const rect = mount?.current?.getBoundingClientRect();

    //check to see if you are hovered on Scroll bar
    if (interactiveState !== InteractiveState.ScrollBar && interactiveState !== InteractiveState.ScrollBarThumb) {
      timelineGL.hoveredOnScrollBarThumb(
        (event.clientX - rect.left) * window.devicePixelRatio,
        event.clientY - rect.top - timelineGL.timelineStart
      );
    }

    // handle moving camera
    if (isAnimating && interactiveState === InteractiveState.Pan) {
      if (Math.abs(event.movementX) > 3 || Math.abs(event.movementY) > 3) {
        shouldDeselectRef.current = false;
      }
      timelineGL.moveCameraByXPixels(-event.movementX);
      // Set user assigned scale when camera is moved by user DRAGGING mouse.
      debouncedSetScale();
    } else if (interactiveState === InteractiveState.Reassign) {
      const yPos = event.clientY - rect.top;
      updateReassignedEvents(selectedEvents, yPos);
    } else if (
      interactiveState === InteractiveState.ScrollBar ||
      interactiveState === InteractiveState.ScrollBarThumb
    ) {
      timelineGL.dragScrollBarThumb(event.movementY);
    } else if (canUpdateHoveredFlightRef.current) {
      canUpdateHoveredFlightRef.current = false;
      updateHoveredFlight(event.clientX, event.clientY);
      setTimeout(() => (canUpdateHoveredFlightRef.current = true), 33);

      //time hovered
      const clientX = event.clientX - rect.left;
      const timeHovered: number = timelineGL.pixelCoordsToEpochTime(clientX * window.devicePixelRatio);
      dispatch(setHoveredTime(timeHovered));
    }
  };

  const handleDoubleClick = (mouseEvent: React.MouseEvent): void => {
    mouseEvent.preventDefault();
    //clear the crew connections
    if (crewConnections) {
      dispatch(setCrewConnections([], windowId));
    }
  };

  const handleWheel = (event: React.WheelEvent): void => {
    event.stopPropagation();
    if (interactiveState === InteractiveState.LockTimeline) {
      return;
    }
    if (timelineGL) {
      let axisToUse = event.deltaY;
      if (checkOS() === "macOS") {
        if (event.shiftKey) {
          axisToUse = event.deltaX;
        } else {
          axisToUse = event.deltaY;
        }
      }
      if (Math.floor(axisToUse) === 0) {
        return;
      }
      const delta = axisToUse >= 1 ? 100 : -100;

      if (event.shiftKey || event.altKey) {
        const [minTime, maxTime] = timelineGL.getTimeRange();

        const rect = mount?.current?.getBoundingClientRect();
        const x = (event.clientX - rect.left) * window.devicePixelRatio;

        const timeX = minTime + timelineGL.pixelCoordsToTimeAxis(x);
        timelineGL.zoomCamera(delta);
        const newPixels = timelineGL.timeAxisToPixelCoords(timeX) * window.devicePixelRatio;
        timelineGL.moveCameraByXPixels(newPixels - x);
        // Set user assigned scale when camera is moved by user ZOOM.
        debouncedSetScale();
        // Check scale presets for nearest match and set label if not already done.
        const currentScale = maxTime - minTime;
        const nearestScaleLabel = findNearestViewportScale(currentScale);
        if (viewScale !== nearestScaleLabel) {
          setViewScale(nearestScaleLabel);
        }

        //check if more flight data needs to be loaded
        if (dataLoadedCheck && typeof dataLoadedCheck === "function") {
          dataLoadedCheck(minTime, maxTime);
        }
      } else {
        timelineGL.moveCameraByYPixels(axisToUse > 0 ? 70 : -70);
      }

      if (!isAnimating) {
        animate();
      }
    }
  };

  const updateHoveredFlight = (clientX: number, clientY: number): void => {
    const [minTime] = timelineGL.getTimeRange();
    const rect = mount?.current?.getBoundingClientRect();
    const scaledX = (clientX - rect.left) * window.devicePixelRatio;
    const y = clientY - rect.top; //  * window.devicePixelRatio;
    const xTime = new Date(minTime + timelineGL.pixelCoordsToTimeAxis(scaledX));
    //const aircraftIndex = timelineGL.getAircraftFromYPixel(y);
    //check if you are on a flight...
    const aircraftInfo = timelineGL.getAircraftIfOnEventTarget(y);
    const aircraftIndex = aircraftInfo.aircraftIndex;
    const isScheduled = aircraftInfo.isScheduled;
    const prevHoveredEvent = timelineGL.getHoveredEvent();

    const aircraft = filteredAircraft[aircraftIndex];
    if (!aircraft) {
      if (prevHoveredEvent) {
        //redraw
        timelineGL.setHoveredEvent(null);
        dispatch(setHoveredEvent(null));
        if (!isAnimating) animate();
      }
      return;
    }
    const aircraftEvents = flights[aircraft._id];
    //!maybe I should add a sched flight to the aircraft array and also see if those flights have a sched bar drawn etc
    if (aircraftEvents) {
      let hoveredEvent = binarySearchAircraftEvents(
        isScheduled
          ? aircraftEvents.flights.slice().sort((a, b) => a.scheduledOut?.valueOf() - b.scheduledOut?.valueOf()) //I need to use slice so I don't mutate the array
          : aircraftEvents.flights,
        xTime.getTime(),
        0,
        aircraftEvents.flights.length - 1,
        isScheduled
      );
      if (!hoveredEvent) {
        hoveredEvent = searchGroundEvents(
          aircraftEvents.groundEvents,
          xTime.getTime(),
          0,
          aircraftEvents.groundEvents.length - 1
        );
      }

      const prevId = prevHoveredEvent ? prevHoveredEvent._id : null;
      const newId = hoveredEvent ? hoveredEvent._id : null;
      // If hovered event has changed, set the new value and redraw timeline.
      if (prevId !== newId) {
        timelineGL.setHoveredEvent(hoveredEvent);
        dispatch(setHoveredEvent(hoveredEvent));
        if (!isAnimating) animate();
      }
    } else if (!aircraftEvents && prevHoveredEvent) {
      timelineGL.setHoveredEvent(null);
      dispatch(setHoveredEvent(null));
    }
  };

  /**
   * Use Effect for creating timeline and/or starting animations
   * @returns function to call when unmounting
   * Now gets called if filteredAircraft array changed (when filters aircraft list)
   */
  useEffect(() => {
    if (!timelineGL) {
      return;
    }
    if (!flightDataLoading.length) {
      timelineGL.updateFlights(flights, filteredAircraft, flightErrorsMap, highlightFlightSettings);
      timelineGL.setMaxRows(allAircraft.length);
    }
  }, [
    timelineGL,
    filteredAircraft,
    currentDate,
    flightErrorsMap,
    flights,
    flightDataLoading.length,
    allAircraft.length,
    highlightFlightSettings,
  ]);

  //initial loading of data
  useEffect(() => {
    if (Object.keys(timeIntervalLoaded).length === 0) {
      dataLoadedCheck(startDate?.valueOf(), endDate?.valueOf());
    }
  }, [dataLoadedCheck, endDate, startDate, timeIntervalLoaded]);

  const handleChangeOffsetHours = useCallback(
    (offset: number) => {
      if (!timelineGL) {
        return;
      }
      const prevOffset = timelineGL.getCurrentDateOffsetHours();
      if (offset === prevOffset) {
        return;
      }
      // Anchor to call time, not mount time: this can fire on a recenter/rescale
      // long after mount, so the window must be relative to "now" at that moment.
      const now = new Date();
      const timeStart = new Date(now.getTime() - startBuffer);
      const timeEnd = new Date(now.getTime() + endBuffer);
      const [xmin, xmax] = [timeStart.getTime(), timeEnd.getTime()];
      //is there an offset?
      const viewScale = timelineGL.getViewScale();
      const screenMid = offset ? (xmin + 2 * offset * hourInMilliseconds + xmax) / 2 : (xmin + xmax) / 2;
      const timeShift = timeScales[viewScale as keyof typeof timeScales] / 2;
      const viewableAreaStart = screenMid - timeShift;
      const viewableAreaEnd = screenMid + timeShift;
      // Set initial timeline viewable area and save to user scale setting.
      timelineGL.setTimeAxis(viewableAreaStart, viewableAreaEnd);
      dispatch(setUserSetScale([viewableAreaStart, viewableAreaEnd]));
    },
    [dispatch, endBuffer, startBuffer, timelineGL]
  );

  useEffect(() => {
    if (!timelineGL) {
      return;
    }
    handleChangeOffsetHours(followCurrentDate ? currentDateOffsetHours : 0);
    timelineGL.setCurrentDateOffsetHours(followCurrentDate ? currentDateOffsetHours : 0);
  }, [timelineGL, followCurrentDate, currentDateOffsetHours, handleChangeOffsetHours]);

  useEffect(() => {
    if (!timelineGL) {
      return;
    }
    //When should this be running? I think only on mount right? so when I open a new flight timeline or when I change the row height.
    // Anchor to call time so a later re-run (e.g. timelineGL change) recomputes from "now".
    const now = new Date();
    const timeStart = new Date(now.getTime() - startBuffer);
    const timeEnd = new Date(now.getTime() + endBuffer);
    const [xmin, xmax] = [timeStart.getTime(), timeEnd.getTime()];
    //is there an offset?
    const offset = timelineGL.getCurrentDateOffsetHours();
    const viewScale = timelineGL.getViewScale();
    const screenMid = offset ? (xmin + 2 * offset * hourInMilliseconds + xmax) / 2 : (xmin + xmax) / 2;
    const timeShift = timeScales[viewScale as keyof typeof timeScales] / 2; //use the viewScale rather than the default flightTimelineViewScale
    const viewableAreaStart = screenMid - timeShift;
    const viewableAreaEnd = screenMid + timeShift;
    // Set initial timeline viewable area and save to user scale setting.
    timelineGL.setTimeAxis(viewableAreaStart, viewableAreaEnd);
    dispatch(setUserSetScale([viewableAreaStart, viewableAreaEnd]));
  }, [endBuffer, startBuffer, timelineGL, dispatch]);

  useEffect(() => {
    //wait until the customer settings are loading
    //if there already exists a timeGL get out of here
    if (!isCustomerStructuresLoaded || timelineGL) {
      return;
    }
    const width = mount?.current?.parentElement?.clientWidth;
    const height = mount?.current?.parentElement?.clientHeight;
    //you only want to hit this once per flight display window
    const timeline = new TimelineGL(
      mount?.current,
      width,
      height,
      themeDefaults?.timelineFont ?? "Arial",
      themeDefaults?.baseFontSize ?? 10,
      flightFlags,
      timelineSettings
    );
    setTimelineGL(timeline);
    dispatch(setTimelineGLs([...timelineGLs, { windowId: windowId, timelineGL: timeline }]));
    mount?.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightFlags, isCustomerStructuresLoaded, themeDefaults, timelineGL, timelineSettings, dispatch, windowId]);

  /**
   * RETURN function triggered on unmount of canvas.
   */
  useEffect(() => {
    return () => {
      if (timelineGL?.loseContextExt) {
        const filteredTimelines = timelineGLs?.filter((timeline: any) => timeline.windowId !== windowId);
        if (filteredTimelines) {
          dispatch(setTimelineGLs(filteredTimelines));
        }
        // The following will remove the webgl context on unmount if extension is available.
        timelineGL.loseContextExt.loseContext();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, timelineGL?.loseContextExt, windowId]);

  useEffect(() => {
    if (!timelineGL) {
      return;
    }
    timelineGL.setViewScale(viewScale);
  }, [timelineGL, viewScale]);

  /**
   * !!REMOUNT TIMELINE if aircraft row settings scale is modified
   * This sets a timeline iteration integer (key) which forces a remount of the webgl and it's children
   * This is required as the rowHeight settings are stored as static values inside the class itself and it must be reconstructed to use them.
   */
  useEffect(() => {
    const rowScaleHasChanged = rowPercentage !== aircraftRowSetting.rowHeightPercentage;
    if (timelineGL && rowScaleHasChanged) {
      setRowPercentage(aircraftRowSetting.rowHeightPercentage);
      // setOnMountTimelineGLViewScale(viewScale);
      setTimelineIteration(timelineIteration + 1);
      const currentTimelineGL = timelineGLs?.find((timeline: any) => timeline.windowId === windowId);
      if (currentTimelineGL) {
        const tempTimelineGLs = [...timelineGLs];
        tempTimelineGLs.splice(tempTimelineGLs.indexOf(currentTimelineGL), 1);
        dispatch(setTimelineGLs(tempTimelineGLs));
        setTimelineGL(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineGL, rowPercentage, timelineIteration, aircraftRowSetting.rowHeightPercentage, dispatch, windowId]);

  /**
   * Function created to resize/redraw the timeline viewport (used when surrounding components or window resize forces change in dimensions.)
   */
  const resizeViewport = useCallback(() => {
    setTimeout(() => {
      if (!timelineGL || !mount.current) {
        return;
      }
      if (
        mount.current &&
        mount.current.parentElement &&
        mount.current.parentElement.clientWidth > 0 &&
        mount.current.parentElement.clientHeight > 0
      ) {
        timelineGL.setViewport(mount.current.parentElement.clientWidth, mount.current.parentElement.clientHeight);
        if (!isAnimating) {
          animate();
        }
      }
    }, 0);
  }, [mount, animate, timelineGL]);

  /**
   * Use effect to trigger the resizeViewport function when size or position changes occur in mosaic window.
   */
  useEffect(() => path && resizeViewport(), [resizeViewport, path]);

  /**
   * New variable and function to hide/display aircraft info push bar
   */
  const [displayACInfoBar, setDisplayACInfoBar] = useState<boolean>(false);
  const toggleAircraftInfoBar = useCallback((): void => {
    setDisplayACInfoBar((state) => !state);
  }, []);

  /**
   * Trigger a resize of viewport if aircraft info push window is triggered.
   * Values used match materials transition values for enteringScreen / leavingScreen (plus 50ms)
   */
  useEffect(() => {
    const delay = displayACInfoBar ? 550 : 250;
    setTimeout(() => {
      if (!isAnimating) resizeViewport();
    }, delay);
  }, [displayACInfoBar, showAircraftStatus, showMaintenanceCounters, resizeViewport]);

  const getAircraftFromEvent = useCallback(
    (eventToFind: IFlight | IGroundEvent) => {
      for (const aircraft of filteredAircraft) {
        const aircraftFlights = flights[aircraft._id];
        if (!aircraftFlights) {
          continue;
        }
        const eventProp = isFlight(eventToFind) ? "flights" : "groundEvents";
        const eventIndex = aircraftFlights[eventProp].findIndex(
          (flight: IFlight | IGroundEvent) => flight._id === eventToFind._id
        );
        if (eventIndex >= 0) {
          return aircraftFlights[eventProp][eventIndex];
        }
      }
      return null;
    },
    [filteredAircraft, flights]
  );

  /**
   * useEffect to update Flight Errors
   */
  useEffect(() => {
    if (!flights || !timelineGL) {
      return;
    }
    timelineGL.updateErrors(flights, flightErrorsMap, highlightFlightSettings);
  }, [timelineGL, flights, flightErrorsMap, highlightFlightSettings]);

  /**
   * useEffect to update flights when flights data changes
   */
  useEffect(() => {
    if (!flights || !timelineGL) {
      return;
    }

    timelineGL.updateFlights(flights, filteredAircraft, flightErrorsMap, highlightFlightSettings);
    timelineGL.setMaxRows(filteredAircraft.length);

    if (!isAnimating) {
      animate();
    }
  }, [
    timelineGL,
    selectedEvents,
    filteredAircraft,
    dispatch,
    getAircraftFromEvent,
    flights,
    animate,
    flightErrorsMap,
    flightDataLoading.length,
    allAircraft.length,
    highlightFlightSettings,
  ]);

  useEffect(() => {
    if (!timelineGL) {
      return;
    }
    const hoveredEvent = timelineGL.getHoveredEvent();
    if (hoveredEvent) {
      timelineGL.setHoveredEvent(getAircraftFromEvent(hoveredEvent));
    }
    timelineGL.setSelectedEvents(selectedEvents);
  }, [timelineGL, selectedEvents, getAircraftFromEvent]);

  useEffect(() => {
    if (!timelineGL) {
      return;
    }
    timelineGL.setCrewConnections(crewConnections ?? [], filteredAircraft ?? []); //canvas
  }, [timelineGL, crewConnections, filteredAircraft]);

  const start = useCallback(() => {
    frameIdRef.current = requestAnimationFrame(animate);
  }, [animate]);

  // Re-Render Timeline if selected events are modified.
  useEffect(() => {
    if (!!start && !isAnimating) {
      start();
    }
  }, [start, selectedEvents]);

  const updateCurrentTime = useCallback(() => {
    if (!timelineGL) {
      return;
    }
    const lastTimePixels = timelineGL.timeAxisToPixelCoords(lastUpdatedTime.current);
    const currentTimePixels = timelineGL.timeAxisToPixelCoords(currentTimeRef.current);
    if (!isAnimating && currentTimePixels - lastTimePixels >= 1) {
      //how much have we moved?
      const movedPixels = currentTimePixels - lastTimePixels;
      lastUpdatedTime.current = currentTimeRef.current;
      if (followCurrentDate) timelineGL.moveCameraByXPixels(movedPixels);
      animate();
    }
  }, [timelineGL, animate, followCurrentDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      currentTimeRef.current = Date.now();
      updateCurrentTime();
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [timelineGL, updateCurrentTime]);

  useEffect(() => {
    if (!timelineGL) {
      return;
    }
    timelineGL.setHoveredEvent(hoveredEvent);
    if (!!start && !isAnimating) {
      start();
    }
  }, [start, hoveredEvent, timelineGL]);

  const flashFocusedEvent = useCallback(() => {
    const defaultColor = timelineGL.getFocusedEventsOutlineColor();
    timelineGL.setFocusedEventsOutlineColor(defaultColor);
    start(); //this is here to have things re-render but I don't understand it...
  }, [start, timelineGL]);

  useEffect(() => {
    //if selected events changed & there exists selected events flash their color
    //and those events are to be focused
    //is there a cleaner way to evaluate shouldFocus?
    if (
      timelineGL &&
      focusedEvent?.length > 0 &&
      (shouldFocus === "first" || shouldFocus === "last" || shouldFocus === "all")
    ) {
      timelineGL.setFocusedEvents(focusedEvent);
      // change the outline color
      const changeFocusedEventsOutline = setInterval(() => {
        flashFocusedEvent();
      }, 200);
      //stop changing the outline color
      setTimeout(() => {
        clearInterval(changeFocusedEventsOutline);
        timelineGL.setFocusedEvents([]);
        dispatch(setFocusedEvents({ events: [], shouldFocus: "none" }));
        //redraw so that the event outlines are back to either hhovered or selected
        start();
      }, 1000);
    }
  }, [dispatch, flashFocusedEvent, focusedEvent, shouldFocus, start, timelineGL]);

  const handleContext = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();
      if (!timelineGL) {
        return;
      }
      //if you are reassigning flights and you right click on the timeline
      //exit out of mode but do not select the day/aircraft
      if (interactiveState === InteractiveState.Reassign) {
        timelineGL.setReassignedFlights([]);
        dispatch(setInteractiveState(InteractiveState.Pan, windowId));
        return;
      }
      const hoveredEvent = timelineGL.getHoveredEvent();

      if (hoveredEvent) {
        // If the hovered event is not already selected, select it
        if (!selectedEvents.some((e) => e._id === hoveredEvent._id)) {
          dispatch(setSelectedEvents([hoveredEvent]));
        }
        setIsActive(false);
        dispatch(setIsAircraftContextOpen(false, windowId));
        dispatch(setIsFlightContextOpen(true, windowId));
      } else {
        // If not hovering an event, check for aircraft row
        const [minTime] = timelineGL.getTimeRange();
        const rect = mount?.current?.getBoundingClientRect();
        const x = (event.clientX - rect.left) * window.devicePixelRatio;
        const xTime = new Date(minTime + timelineGL.pixelCoordsToTimeAxis(x));

        const y = event.clientY - rect.top;
        const aircraftIndex = timelineGL.getAircraftFromYPixel(y);
        const aircraft = filteredAircraft[aircraftIndex];

        if (aircraft) {
          dispatch(setBulkAssignmentMode(xTime, [aircraft._id as string], windowId));
          setIsActive(false);
          dispatch(setIsAircraftContextOpen(false, windowId));
          dispatch(setIsFlightContextOpen(true, windowId));
        } else {
          setIsActive(false);
          dispatch(setIsAircraftContextOpen(false, windowId));
          dispatch(setIsFlightContextOpen(true, windowId));
        }
      }
    },
    [dispatch, filteredAircraft, interactiveState, selectedEvents, setIsActive, timelineGL, windowId]
  );

  const handleKeyUp = (event: React.KeyboardEvent): void => {
    switch (event.key) {
      case " ":
        if (interactiveState === InteractiveState.LockTimeline) {
          dispatch(setInteractiveState(InteractiveState.Pan, windowId));
        }
        break;
      default:
        break;
    }
  };

  // Filter out any GEs that arent GTBs
  const draggableEvents = useMemo(() => {
    return selectedEvents.filter(
      (event: IFlight | IGroundEvent) =>
        (!isFlight(event) &&
          (event.eventType?.isGroundTurnBack || event.eventType?.isCrewable || event.eventType?.isSim)) ||
        isFlight(event)
    );
  }, [selectedEvents]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent): void => {
      if (interactiveState === InteractiveState.LockTimeline) {
        return;
      }
      switch (event.key) {
        case " ":
          if (draggableEvents.length > 0) {
            // Only activate drag mode is there are selected events.
            dispatch(setInteractiveState(InteractiveState.LockTimeline, windowId));
          }
          break;
        case "Tab": {
          event.preventDefault();
          const [minTime] = timelineGL.getTimeRange();
          const rect = mount?.current?.getBoundingClientRect();
          let newDay = null;
          const direction = event.shiftKey ? -1 : 1;
          if (selectedDay) {
            newDay = new Date(selectedDay);
            newDay.setUTCDate(selectedDay.getUTCDate() + direction);
          } else {
            newDay = new Date(minTime + timelineGL.pixelCoordsToTimeAxis(rect.width / 2.0));
          }
          dispatch(setSelectedDay(newDay, windowId));

          timelineGL.moveCameraToTime(newDay.getTime());
          break;
        }
        case "Escape":
          dispatch(setBulkAssignmentMode(null, [], windowId));
          break;
        default:
          break;
      }
    },
    [dispatch, draggableEvents.length, interactiveState, selectedDay, timelineGL, windowId]
  );

  /**
   * Get's the current viewport scale and returns the closest available preset name.
   */
  const findNearestViewportScale = (currentScale: number): string => {
    const presetScaleValues = Object.values(timeScales);
    const presetScaleLabels = Object.keys(timeScales);
    const scaleIndex = indexOfClosest(presetScaleValues, currentScale);
    return presetScaleLabels[scaleIndex];
  };

  /**
   * Set's the min and max viewport scale to display one of 5 available presets.
   * Uses the current screen's center point as reference (so as to not move the user's intended location)
   * @param scale -
   */
  const setViewportScale = useCallback(
    (scale?: string | number[]) => {
      if (!timelineGL) {
        return;
      }
      if (!!scale && typeof scale === "string" && timeScales.hasOwnProperty(scale)) {
        const [xmin, xmax] = timelineGL.getTimeRange();
        const screenMid = (xmin + xmax) / 2;
        const timeShift = timeScales[scale as keyof typeof timeScales] / 2;
        setViewScale(scale);
        timelineGL.setTimeAxis(screenMid - timeShift, screenMid + timeShift);
      } else if (!!scale && Array.isArray(scale) && scale.length === 2) {
        const [minTime, maxTime] = scale;
        dataLoadedCheck(minTime, maxTime);
        timelineGL.setTimeAxis(minTime, maxTime);
      }
      if (!isAnimating) {
        animate();
      }
    },
    [timelineGL, animate, dataLoadedCheck]
  );

  useEffect(() => {
    if (viewScale !== flightTimelineViewScale) {
      setViewportScale(flightTimelineViewScale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightTimelineViewScale]);

  /**
   * Helper function for both the jump to flight function on the timeline & the quick search for a flight on the timeline
   * @param selectedFlight -
   */
  const jumpToEventHelper = useCallback(
    (selectedEvent: IFlight | IGroundEvent | IImportFlight) => {
      if (timelineGL && !!selectedEvent && !!selectedEvent.aircraft) {
        const newYScroll = timelineGL.jumpToFlight(selectedEvent, filteredAircraft);
        if (aircraftView.current && newYScroll >= 0) {
          aircraftView.current.scroll(0, newYScroll);
        }
      }
    },
    [timelineGL, filteredAircraft]
  );

  const jumpToEventGroup = useCallback(
    (selectedEvents?: (IFlight | IGroundEvent | IImportFlight)[]): boolean => {
      if (selectedEvents.length === 0) {
        return false;
      }

      const earliestDep = selectedEvents.reduce(
        (eventA: IFlight | IGroundEvent | IImportFlight, eventB: IFlight | IGroundEvent | IImportFlight) => {
          const Atime = isFlight(eventA) ? new Date(eventA.actualOff) : new Date(eventA.actualStart);
          const Btime = isFlight(eventB) ? new Date(eventB.actualOff) : new Date(eventB.actualStart);
          return Atime < Btime ? eventA : eventB;
        }
      );
      const latestArr = selectedEvents.reduce(
        (eventA: IFlight | IGroundEvent | IImportFlight, eventB: IFlight | IGroundEvent | IImportFlight) => {
          const Atime = isFlight(eventA) ? new Date(eventA.actualOn) : new Date(eventA.actualEnd);
          const Btime = isFlight(eventB) ? new Date(eventB.actualOn) : new Date(eventB.actualEnd);
          return Atime > Btime ? eventA : eventB;
        }
      );
      const startTime = isFlight(earliestDep)
        ? new Date(earliestDep.actualOff).getTime() - timeScales.day
        : new Date(earliestDep.actualStart).getTime() - timeScales.day;
      const endTime = isFlight(latestArr)
        ? new Date(latestArr.actualOn).getTime() + timeScales.day
        : new Date(latestArr.actualEnd).getTime() + timeScales.day;

      // Decide if timeline needs to be zoomed out or current scale already accomodates new focused events.
      const [currentStart, currentEnd] = timelineGL.getTimeRange();
      const newScale =
        endTime - startTime >= currentEnd - currentStart ? [startTime, endTime] : [currentStart, currentEnd];

      setViewportScale(newScale);
      return true;
    },
    [setViewportScale, timelineGL]
  );

  // Jump to a supplied Date (if not provided, jump to current date) and set viewportScale to 'day'
  const setFocusOnDate = useCallback(
    (jumpDate: Date): void => {
      if (!timelineGL) {
        return;
      }
      const jumpTime =
        typeof jumpDate === "object" && typeof jumpDate.getMonth === "function"
          ? jumpDate.getTime()
          : new Date().getTime();
      timelineGL.moveCameraToTime(jumpTime);
      setViewportScale("day");

      // Save user Scale
      const scaleStartTime = jumpTime - timeScales.day / 2;
      const scaleEndTime = jumpTime + timeScales.day / 2;
      dispatch(setUserSetScale([scaleStartTime, scaleEndTime]));
      // LOAD data if not already loaded.
      if (dataLoadedCheck && typeof dataLoadedCheck === "function") {
        dataLoadedCheck(scaleStartTime, scaleEndTime);
      }
    },
    [dispatch, setViewportScale, timelineGL, dataLoadedCheck]
  );

  useEffect(() => {
    if (timelineGL && !!shouldFocus && focusedEvent.length > 0) {
      switch (shouldFocus) {
        case "all":
          if (focusedEvent.length === 1) jumpToEventHelper(focusedEvent[0]);
          jumpToEventGroup(focusedEvent);
          //dispatch(setSelectedEvents({ events: selectedEvents, shouldFocus: "none" }));
          break;
        case "first":
          jumpToEventHelper(focusedEvent[0]);
          jumpToEventGroup([focusedEvent[0]]);
          //dispatch(setSelectedEvents({ events: selectedEvents, shouldFocus: "none" }));
          break;
        case "last":
          jumpToEventHelper(focusedEvent[focusedEvent.length - 1]);
          jumpToEventGroup([focusedEvent[focusedEvent.length - 1]]);
          //dispatch(setSelectedEvents({ events: selectedEvents, shouldFocus: "none" }));
          break;
        default:
      }
    }
  }, [timelineGL, shouldFocus, jumpToEventHelper, jumpToEventGroup, focusedEvent, dispatch]);

  /**
   * Helper Function that will both focus in on a selected flight as well as jump to it on the timeline.
   * @param selectedFlight -
   */
  const setFocusOnEvent = useCallback(
    (eventObj: IFlight | IGroundEvent): void => {
      const event: IFlight | IGroundEvent | any = eventObj;
      if (!timelineGL) {
        return;
      }

      if (!event) {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Unable to focus event, please search the timeline.",
          })
        );
        return;
      }

      if (!event.hasOwnProperty("aircraft") || !event.aircraft) {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Unable to focus event without an assigned aircraft.",
          })
        );
        return;
      }

      const startTime = isFlight(event) ? new Date(event.actualOff).getTime() : new Date(event.actualStart).getTime();
      const endTime = isFlight(event) ? new Date(event.actualOn).getTime() : new Date(event.actualEnd).getTime();

      const scaleStartTime = startTime - timeScales.hour;
      const scaleEndTime = endTime + timeScales.hour;
      timelineGL.setTimeAxis(scaleStartTime, scaleEndTime);
      jumpToEventHelper(event);

      // Save user Scale
      dispatch(setUserSetScale([scaleStartTime, scaleEndTime]));
    },
    [dispatch, jumpToEventHelper, timelineGL]
  );

  const renderNoAircraftMessage = (): any => {
    if (!!timelineGL && !!filteredAircraft && filteredAircraft.length === 0) {
      return (
        <Grid item className="timeline_no_flights">
          <Typography variant="body1" style={{ margin: 20 }}>
            {quickFilterActive
              ? "There are no Aircraft visible, none of the aircraft in the quick filter match the current aircraft filter. Reset the quick filter or adjust the aircraft filter menu."
              : "There are no Aircraft visible, please select an aircraft type or grouping from the aircraft filter menu."}
          </Typography>
        </Grid>
      );
    }
    return null;
  };

  return (
    <Grid sx={timelineStyles()} ref={timelineContainer} container>
      <Grid item sx={timelineHeaderStyles(theme)}>
        <FlightTimelineHeader
          id={windowId}
          path={props.path}
          timelineContainer={timelineContainer}
          selectedEvents={selectedEvents}
          setFocusOnEvent={setFocusOnEvent}
          setFocusOnDate={setFocusOnDate}
          setViewportScale={setViewportScale}
          viewScale={viewScale}
          startDate={startDate}
          endDate={endDate}
          setActiveEventModal={props.setActiveEventModal}
          resizeViewport={resizeViewport}
          timelineLocked={interactiveState === InteractiveState.LockTimeline}
          setTimelineLocked={() => {
            dispatch(
              setInteractiveState(
                interactiveState === InteractiveState.LockTimeline
                  ? InteractiveState.Pan
                  : InteractiveState.LockTimeline,
                windowId
              )
            );
          }}
          isDisplayICAO={isFlightDisplayICAO}
          setDisplayICAO={toggleICAO}
          isDisplayUTC={isFlightDisplayUTC}
          setDisplayUTC={toggleUTC}
          highlightIsActive={highlightIsActive}
          highlightConditions={highlightConditions}
          showMaintenanceCounters={showMaintenanceCounters}
          setShowMaintenanceCounters={setShowMaintenanceCounters}
          selectedAircraftIds={selectedAircraftIds}
          displayedAircraftIds={displayedAircraftIds}
          onSelectAllAircraft={handleSelectAllAircraft}
        />
      </Grid>

      <Grid item sx={timelineBodyStyles(theme, aircraftRowSetting)} key={timelineIteration}>
        <Grid container direction="row" className="timeline_content" style={{ position: "relative" }}>
          <Grid
            container
            direction="column"
            className={`timeline_aircraft_column ${showAircraftStatus ? "expanded" : ""} ${showMaintenanceCounters ? "mx_counters_visible" : ""}`}
          >
            <Grid container className="aircraft_column_header" wrap="nowrap" alignItems="center">
              <TimelineTime setFocusOnDate={setFocusOnDate} windowId={windowId} />
              <IconButton
                className="toggle_aircraft_status_btn"
                onClick={() => setShowAircraftStatus(!showAircraftStatus)}
                tooltipProps={{
                  title: showAircraftStatus ? "Hide Aircraft Status" : "Show Aircraft Status",
                  enterDelay: 1000,
                  placement: "right",
                }}
                size="large"
              >
                {showAircraftStatus ? <ArrowLeft /> : <ArrowRight />}
              </IconButton>
              {showAircraftStatus && <Typography className="status_title">Aircraft Status</Typography>}
            </Grid>

            <Grid ref={aircraftView} item className="aircraft_identity">
              {filteredAircraft.map((ac: IAircraft, index: number) => (
                <AircraftIdentityRow
                  key={`aircraft_${ac._id}`}
                  aircraft={ac}
                  index={index}
                  windowId={windowId}
                  selected={selectedAircraftArray?.includes(ac._id as string) ?? false}
                  reorderable={quickFilterActive}
                  showMaintenanceCounters={showMaintenanceCounters}
                  showAircraftStatus={showAircraftStatus}
                  onSelect={showAircraftInfo}
                  onOpenContextPanel={showAircraftContextPanel}
                  onMove={handleQuickFilterMove}
                  onDragHover={handleQuickFilterDragHover}
                />
              ))}
            </Grid>
          </Grid>

          <Grid
            item
            className={`timeline_flights_column ${displayACInfoBar ? "aircraftinfo_open" : ""} ${
              showAircraftStatus ? "aicraftstatus_open" : ""
            } ${showMaintenanceCounters ? "mx_counters_open" : ""}`}
          >
            <Grid
              item
              id={`timeline_${windowId}`}
              className="timeline_webgl_container"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              // onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchDown}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchUp}
              onWheel={handleWheel}
              onContextMenu={handleContext}
              onPointerLeave={handleMouseLeave}
              onPointerUp={(event: PointerEvent<HTMLDivElement>) => {
                if (captureMode && mount) {
                  exitCaptureMode(event.pointerId);
                }
              }}
              onDoubleClick={handleDoubleClick}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
              style={{ position: "relative" }}
              onBlur={() => setIsActive(false)}
              onFocus={() => setIsActive(true)}
            >
              <div ref={drop} style={{ flexGrow: 1 }}>
                <div
                  tabIndex={0}
                  id={props.windowId + "-timelineGL"}
                  className="timeline_webgl_canvas"
                  key={props.windowId + "-timelineGL"}
                  ref={mount}
                />
              </div>

              {highlightConditions && timelineGL && (
                <Grid item sx={flightHighlightBarStyles(theme)} className={`${highlightIsActive ? "active" : ""}`}>
                  <IconButton
                    className={"highlight_toggle_btn "}
                    onClick={handleToggleHighlight}
                    tooltipProps={{
                      title: highlightConditions,
                      enterDelay: 1000,
                      placement: "top",
                    }}
                    size="large"
                  >
                    <FeaturedPlayListIcon />
                  </IconButton>
                  <Typography variant="caption" className="highlight_label">
                    {`Flight Highlight ${highlightIsActive ? "Active" : ""}`}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {draggableEvents.length > 0 && (
              <SelectedFlightsDragLayer
                events={draggableEvents}
                aircrafts={filteredAircraft}
                timelineGL={timelineGL}
                timelineWindowId={windowId}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <FlightTimelineDatePicker
        anchorID={`timeline_${windowId}`}
        timelineGL={timelineGL}
        display={showTimelineDatePickers}
        setDisplay={setShowTimelineDatePickers}
        debouncedSetScale={debouncedSetScale}
        dataLoadedCheck={dataLoadedCheck}
        refreshTimeline={animate}
      />

      {renderNoAircraftMessage()}
      {!hideHoveredTimeIndicator && timelineGL && (
        <HoveredTime setHoveredTime={timelineGL.setHoveredTime} isAnimating={isAnimating} start={start} />
      )}
    </Grid>
  );
};
